import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import { usePageInfo } from '@op/hooks/common';
import React, { useEffect, useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import { useHistory } from 'umi';
import useLanguage from '@op/hooks/useLanguage';
import CardSentiment from '@op/pages/accounts/widgets/AccountsCard/Sentiment';
import {
  useRequestComments,
  useRequestStats,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import './index.scss';
import SocialMedia from '@op/pages/stats/components/SocialMedia';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { AnalysisSummaryTypeTab } from '../../types';
import DownloadDialog from '@op/components/DownloadCommon/download-dialog';
import { downloadAction } from '@op/pages/download/utils';
import { analysisTableTab, AnalysisTableTypeTab, socialTabOptions } from '../../constants';
import { AnalysisMetricList } from '@op/pages/download/constants/SocialMediaColumns';

type IAnalysisTable = {
  filter: any;
  gameInfo: any;
  tab: AnalysisSummaryTypeTab;
  wholeGameInfo: any;
  oriFilterValue: any;
  setApiLoading?: (flag: string) => void;
  hasTabChange?: boolean;
  title?: string;
  detailParams?: any;
} & CardCommonType;

export default function AnalysisTable(props: IAnalysisTable) {
  const {
    filter,
    gameInfo,
    tab,
    wholeGameInfo,
    oriFilterValue,
    setApiLoading,
    hasTabChange = true,
    detailParams,
    ...cardProps
  } = props;
  const pageInfoValue = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'sort_isvalid',
    order: 'desc',
  });
  const [tabValue, setTabValue] = useState<'social' | 'comments'>('social');
  const [socialTabValue, setSocialTabValue] = useState<AnalysisTableTypeTab>('social');
  const { formatMessage } = useLanguage();
  const { feedsStats, feedsStatsLoading, requestState } = useRequestStats({
    defaultLoading: true,
  });

  useEffect(() => {
    const { social_total = 0, comments_total = 0 } = feedsStats || {};
    if (social_total === 0 && comments_total > 0) {
      setTabValue('comments');
    }
  }, [feedsStats]);

  const { feedsComments, feedsCommentsLoading, requestComments } =
    useRequestComments({
      finallyFunc: (data) => {
        if (data.total === 0) {
          setApiLoading?.('comments');
        }
      },
    });

  useEffect(() => {
    if (!gameInfo || !filter) return;

    requestState({
      ...filter,
      ...gameInfo,
      translated_to: 'origin',
    });
  }, [filter, gameInfo]);

  const { realFilter, channelType } = useMemo(() => {
    if (!filter) {
      return {
        realFilter: null,
        channelType: '',
      };
    }

    let extraParams: Record<string, any> = {};

    if (socialTabValue === 'social_comments') {
      extraParams = { review_types: ['sub_text', 'sub_video'] };
    } else if (socialTabValue === 'social_video_posts') {
      extraParams = { review_types: ['posts_text', 'posts_video'] };
    }

    const channelType =
      ['google play', 'app store', 'steam'].includes(tab) ||
        tabValue === 'comments'
        ? 'comments'
        : 'social';

    const {
      channel_comments,
      channel_news,
      channel_securitys,
      channel_social,
      channel,
      channel_type,
      ...otherFilterInfo
    } = filter;

    if (channelType === 'comments') {
      return {
        realFilter: {
          ...otherFilterInfo,
          ...extraParams,
          channel: channel || channel_comments,
          channel_type: channelType || 'comments',
        },
        channelType,
      };
    }

    return {
      realFilter: {
        ...otherFilterInfo,
        ...extraParams,
        channel: channel || channel_social,
        channel_type: channelType || 'social',
      },
      channelType,
    };
  }, [filter, tabValue, tab, socialTabValue]);

  const [isAll, isGameStore] = useMemo(() => {
    const isAll = tab === 'community' || tab === 'total';
    return [isAll, !isAll || channelType === 'comments'];
  }, [tab, channelType]);

  const [downloadVisible, setDownloadVisible] = useState(false);

  return (
    <CardCommon
      title={formatMessage({
        id: isAll ? 'Feeds' : 'Game Store Reviews',
      })}
      hasTitleArrow={isAll}
      loading={feedsStatsLoading || !realFilter}
      loadingHeight={400}
      handleTitleOnClick={() => {
        if (!isAll) return;
        buriedBtnPoint('GotoFeeds');
        goStatsDetailAction({
          path: `/opinion/Feeds/Feeds?tab=socialMedia`,
          params: { ...oriFilterValue, ...realFilter },
        });
      }}
      className="dbo-analysis-table"
      hasDownload
      handleDownloadOnClick={() => {
        setDownloadVisible(true);
      }}
      {...cardProps}
    >
      <div className="dbo-analysis-table-select-box">
        {isAll && hasTabChange && (
          <>
            <Segment
              className="segment-font12"
              value={tabValue}
              onChange={(value: 'social' | 'comments') => {
                if (value === 'comments') {
                  pageInfoValue.changePageInfoNoRender();
                  buriedBtnPoint('ReviewTypeTAB', value);
                } else {
                  setSocialTabValue('social');
                }
                setTabValue(value);
              }}
              options={analysisTableTab(
                formatMessage,
                tab === 'community' ? ['comments'] : [],
              )}
            />
            {
              tabValue === 'social' && (
                <Segment
                  className="segment-font12"
                  value={socialTabValue}
                  onChange={(value: AnalysisTableTypeTab) => {
                    pageInfoValue.changePageInfoNoRender();
                    buriedBtnPoint('ReviewTypeTAB', value);
                    setSocialTabValue(value);
                  }}
                  options={socialTabOptions(formatMessage)}
                />
              )
            }
          </>
        )}

        <CardSentiment
          sentimentsData={feedsStats}
          className={`keywordbox-hd-itembox --flex dbo-keywordbox-sentimentbox-border ${isGameStore ? '--noborder' : ''}`}
          hideOptions={isGameStore}
          title={
            isGameStore ? formatMessage('Mentions by sentiment') : undefined
          }
        />
      </div>

      <SocialMedia
        sortBy={sortBy}
        gameInfo={gameInfo}
        filter={realFilter}
        hasPageable
        channelType={channelType}
        comments={feedsComments}
        loading={feedsCommentsLoading}
        pageInfoValue={pageInfoValue}
        setSortBy={setSortBy}
        requestFunc={requestComments}
        setApiLoading={setApiLoading}
        detailParams={detailParams}
      />

      <DownloadDialog
        visible={downloadVisible}
        className="dbo-analysis-table-download-dialog"
        setVisible={setDownloadVisible}
        download={(params: any) => {
          const fileName = `${tabValue} Feeds - DataBrain Opinions`;

          downloadAction({
            key: 'SocialMediaDownload',
            fileName,
            // sheetName: isGameStore ? 'Game Store' : 'Social Media',
            auxiliaryParams: {
              metrics: params.metrics,
            },
            filter: {
              ...realFilter,
              ...gameInfo,
              sort_item: sortBy.by,
              sort_model: sortBy.order,
            },
          });
        }}
        gameInfo={gameInfo}
        selectMetric={AnalysisMetricList({
          hideSentimentRating: false,
          gameInfo,
        }).map((item) => item.value)}
        showList={[
          {
            label: 'Metrics',
            type: 'checkbox',
            list: AnalysisMetricList({ hideSentimentRating: false, gameInfo }),
          },
        ]}
      ></DownloadDialog>
    </CardCommon>
  );
}
